/**
 * Excel financials card — for clients not on Xero/Prosaic.
 *
 * Firm downloads a pre-filled ClientOps quarterly template, sends it to the
 * client, then uploads the completed workbook back here. Parsed values feed
 * into the SAME `client_financials` collection Xero/Prosaic feeds, so the
 * portal home cards, Health Score and tax engine treat it identically.
 */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Download, FileSpreadsheet, Loader2, Upload, CheckCircle2, RefreshCw } from "lucide-react";

import { api } from "@/lib/api";
import { formatApiError } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const NZD = new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD", maximumFractionDigits: 0 });

export default function ExcelFinancialsCard({ client, onUploaded }) {
    const [history, setHistory] = useState([]);
    const [busy, setBusy] = useState(false);
    const [preview, setPreview] = useState(null);
    const [pending, setPending] = useState(null);   // { file, previewData }
    const fileRef = useRef(null);

    async function loadHistory() {
        try {
            const { data } = await api.get("/excel-financials/history", { params: { client_id: client.id } });
            setHistory(data.items || []);
        } catch {
            setHistory([]);
        }
    }
    useEffect(() => { if (client?.id) loadHistory(); /* eslint-disable-line */ }, [client?.id]);

    async function downloadTemplate() {
        setBusy(true);
        try {
            const res = await api.get("/excel-financials/template", {
                params: { client_id: client.id },
                responseType: "blob",
            });
            const url = window.URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            const cd = res.headers["content-disposition"] || "";
            const m = cd.match(/filename="([^"]+)"/);
            a.download = m ? m[1] : `ClientOps-Quarterly-${(client.name || "template").replace(/\s+/g, "_")}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Template downloaded — send it to the client.");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    async function onPickFile(f) {
        if (!f) return;
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            const { data } = await api.post("/excel-financials/preview", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setPending({ file: f, preview: data });
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    }

    async function confirmUpload() {
        if (!pending) return;
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", pending.file);
            const { data } = await api.post(`/excel-financials/upload?client_id=${client.id}`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success(`Financials imported — as of ${data.as_of_date}. Portal home is now live.`);
            setPending(null);
            await loadHistory();
            onUploaded?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    const last = history[0];

    return (
        <div className="co-card co-card-pad" data-testid="excel-financials-card">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="co-label flex items-center gap-1.5">
                        <FileSpreadsheet className="h-3.5 w-3.5" /> Quarterly financials — Excel
                    </div>
                    <h3 className="mt-1 font-display text-lg">For clients not on Xero or Prosaic</h3>
                    <p className="mt-1 text-sm text-[#5E636E] max-w-2xl">
                        Download the ClientOps quarterly template, email it to the client, then upload the
                        completed workbook back here. Numbers flow straight into their portal — same 4 hero
                        cards, same Health Score, no double entry.
                    </p>
                </div>
                {last && (
                    <div className="text-right shrink-0 text-xs" data-testid="excel-last-upload">
                        <div className="inline-flex items-center gap-1 text-emerald-700 font-medium">
                            <CheckCircle2 className="h-3.5 w-3.5" /> Last upload
                        </div>
                        <div className="text-[#5E636E] mt-0.5">As of {last.as_of_date}</div>
                        <div className="text-[#5E636E]">by {last.uploaded_by_name || "—"}</div>
                    </div>
                )}
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
                <Button variant="outline" onClick={downloadTemplate} disabled={busy} data-testid="excel-download-template">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Download className="h-4 w-4" /> Download template</>}
                </Button>
                <Button
                    onClick={() => fileRef.current?.click()}
                    disabled={busy}
                    className="bg-[#0E3B2E] hover:bg-[#0B2F25] text-white"
                    data-testid="excel-upload-btn"
                >
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4" /> {last ? "Upload new quarter" : "Upload completed template"}</>}
                </Button>
                {history.length > 0 && (
                    <Button variant="ghost" onClick={() => setPreview({ open: true, items: history })} data-testid="excel-history-btn">
                        <RefreshCw className="h-4 w-4" /> View history ({history.length})
                    </Button>
                )}
                <input
                    ref={fileRef}
                    type="file"
                    accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    className="hidden"
                    onChange={(e) => onPickFile(e.target.files?.[0])}
                    data-testid="excel-file-input"
                />
            </div>

            {/* Preview + confirm dialog */}
            <Dialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
                <DialogContent className="max-w-lg" data-testid="excel-preview-dialog">
                    <DialogHeader>
                        <DialogTitle>Confirm quarterly financials</DialogTitle>
                        <DialogDescription>
                            Review the parsed values before committing. Uploading replaces the previous
                            client_financials row for this client and refreshes their Health Score.
                        </DialogDescription>
                    </DialogHeader>
                    {pending?.preview && (
                        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm" data-testid="excel-preview-values">
                            <PreviewRow k="As of" v={pending.preview.as_of_date} />
                            <PreviewRow k="Cash on hand" v={pending.preview.cash_balance != null ? NZD.format(pending.preview.cash_balance) : "—"} />
                            <PreviewRow k="Revenue (Q)" v={pending.preview.revenue_period != null ? NZD.format(pending.preview.revenue_period) : "—"} />
                            <PreviewRow k="Expenses (Q)" v={pending.preview.expenses_period != null ? NZD.format(pending.preview.expenses_period) : "—"} />
                            <PreviewRow k="Net profit" v={pending.preview.net_profit_period != null ? NZD.format(pending.preview.net_profit_period) : "—"} tone={pending.preview.net_profit_period >= 0 ? "good" : "bad"} />
                            <PreviewRow k="Prior-quarter revenue" v={pending.preview.revenue_prior_period != null ? NZD.format(pending.preview.revenue_prior_period) : "—"} />
                            <PreviewRow k="Tax owed estimate" v={pending.preview.tax_owed_estimate != null ? NZD.format(pending.preview.tax_owed_estimate) : "—"} />
                            <PreviewRow k="Tax set aside" v={pending.preview.tax_set_aside != null ? NZD.format(pending.preview.tax_set_aside) : "—"} />
                            <PreviewRow k="Overdue returns" v={pending.preview.overdue_return_count ?? 0} />
                            <PreviewRow k="Missing docs" v={pending.preview.missing_docs_count ?? 0} />
                            <PreviewRow k="Categories parsed" v={pending.preview.expense_categories?.length || 0} />
                        </dl>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setPending(null)} disabled={busy}>Cancel</Button>
                        <Button
                            className="bg-[#0E3B2E] hover:bg-[#0B2F25] text-white"
                            onClick={confirmUpload}
                            disabled={busy}
                            data-testid="excel-confirm-upload"
                        >
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Commit + refresh portal"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* History dialog */}
            <Dialog open={!!preview?.open} onOpenChange={(o) => !o && setPreview(null)}>
                <DialogContent className="max-w-xl" data-testid="excel-history-dialog">
                    <DialogHeader>
                        <DialogTitle>Excel upload history</DialogTitle>
                        <DialogDescription>Previous quarterly imports for {client?.name || "this client"}.</DialogDescription>
                    </DialogHeader>
                    <ul className="divide-y divide-[#E4E4E7] text-sm max-h-[400px] overflow-auto">
                        {(preview?.items || []).map((h, i) => (
                            <li key={i} className="py-2 flex items-center justify-between gap-3">
                                <div>
                                    <div className="font-medium">As of {h.as_of_date}</div>
                                    <div className="text-[11px] text-[#5E636E]">
                                        {new Date(h.uploaded_at).toLocaleString("en-NZ")} · {h.uploaded_by_name}
                                    </div>
                                </div>
                                <div className="text-right text-xs text-[#5E636E]">
                                    <div>Cash {h.cash_balance != null ? NZD.format(h.cash_balance) : "—"}</div>
                                    <div>Rev {h.revenue_period != null ? NZD.format(h.revenue_period) : "—"}</div>
                                </div>
                            </li>
                        ))}
                    </ul>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setPreview(null)}>Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function PreviewRow({ k, v, tone }) {
    const cls = tone === "good" ? "text-emerald-700 font-medium" : tone === "bad" ? "text-rose-700 font-medium" : "";
    return (
        <>
            <dt className="text-[#5E636E]">{k}</dt>
            <dd className={"tabular-nums text-right " + cls}>{v}</dd>
        </>
    );
}
